import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { AlertTriangle, CircleUser } from "lucide-react";
import { format } from "date-fns";
import { TYPE_META } from "./GuestCard";

const TYPES = [["large", "Large"], ["medium", "Medium"], ["small", "Small"], ["umbrella", "Umbrella"]];
const t = (s) => (s ? (s.split(" ")[1] || "").slice(0, 5) : "");

export const RecapDialog = ({ open, onOpenChange, data, noShowFor, date }) => {
  const allocByLounge = {};
  (data.allocations || []).forEach((a) => { allocByLounge[a.lounge_id] = a; });
  const labelById = {};
  (data.lounges || []).forEach((l) => { labelById[l.id] = l.label; });
  const byType = {};
  (data.lounges || []).forEach((l) => { (byType[l.type] = byType[l.type] || []).push(l); });

  const rows = TYPES.map(([type, label]) => {
    const lounges = byType[type] || [];
    const used = lounges.filter((l) => allocByLounge[l.id]).length;
    const seated = lounges.filter((l) => allocByLounge[l.id]?.status === "arrived").length;
    const ns = lounges.filter((l) => noShowFor(allocByLounge[l.id])).length;
    return { type, label, total: lounges.length, used, seated, ns, pct: lounges.length ? Math.round((used / lounges.length) * 100) : 0 };
  });

  const noShowList = (data.allocations || [])
    .filter((a) => noShowFor(a))
    .map((a) => ({ guest: a.guest_name, lounge: labelById[a.lounge_id], time: a.start_date }));

  const totalSpots = (data.lounges || []).length;
  const totalUsed = (data.allocations || []).length;
  const totalSeated = (data.allocations || []).filter((a) => a.status === "arrived").length;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="recap-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl tracking-wide">
            Daily Recap
            <span className="ml-2 text-sm font-normal text-slate-400">{format(date, "EEE, dd MMM yyyy")}</span>
          </DialogTitle>
          <DialogDescription className="sr-only">Occupancy summary and no-show list for the selected day</DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-3 gap-2">
          <Stat label="Allocated" value={`${totalUsed}/${totalSpots}`} />
          <Stat label="Arrived" value={totalSeated} tone="text-sea-600" icon={<CircleUser size={14} />} />
          <Stat label="No-shows" value={noShowList.length} tone="text-destructive" icon={<AlertTriangle size={14} />} />
        </div>

        <div className="mt-2 space-y-3">
          {rows.map((r) => (
            <div key={r.type} data-testid={`recap-${r.type}`}>
              <div className="mb-1 flex items-center justify-between text-sm">
                <div className="flex items-center gap-2">
                  <span className={`h-2.5 w-2.5 rounded-full ${TYPE_META[r.type].dot}`} />
                  <span className="font-semibold text-slate-700">{r.label}</span>
                </div>
                <span className="tabular-nums text-slate-500">
                  {r.used}/{r.total} used · {r.seated} arrived{r.ns > 0 && <span className="font-semibold text-destructive"> · {r.ns} no-show</span>}
                </span>
              </div>
              <div className="h-2.5 overflow-hidden rounded-full bg-sand-200">
                <div className={`h-full rounded-full ${TYPE_META[r.type].bar}`} style={{ width: `${r.pct}%` }} />
              </div>
            </div>
          ))}
        </div>

        {noShowList.length > 0 && (
          <div className="mt-3 rounded-xl border border-destructive/30 bg-destructive/[0.06] p-3">
            <p className="mb-1.5 flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-destructive">
              <AlertTriangle size={13} /> No-shows ({noShowList.length})
            </p>
            <ul className="space-y-1 text-sm">
              {noShowList.map((n, i) => (
                <li key={`${n.guest}-${n.lounge}-${n.time}-${i}`} className="flex items-center justify-between">
                  <span className="text-slate-700">{n.guest}</span>
                  <span className="text-slate-400">{n.lounge} · {t(n.time)}</span>
                </li>
              ))}
            </ul>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

const Stat = ({ label, value, tone = "text-slate-900", icon }) => (
  <div className="rounded-xl border border-sand-300 bg-sand-50 p-3 text-center">
    <p className="text-[11px] font-medium uppercase tracking-wider text-slate-500">{label}</p>
    <p className={`mt-1 flex items-center justify-center gap-1 font-display text-xl tabular-nums ${tone}`}>
      {icon} {value}
    </p>
  </div>
);
